import { useCallback, useRef, useState } from "react";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import { AttachmentUploadResponse } from "@/entities/attachments/types";
import { ActivityData } from "@/entities/activities/types";
import { useUpdateActivityMutation } from "@/entities/activities/hooks";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  generateTranscriptFilename,
  isValidTranscriptFile,
  ACCEPTED_TRANSCRIPT_EXTENSIONS
} from "@/entities/activities/helpers";

export { isValidTranscriptFile, ACCEPTED_TRANSCRIPT_EXTENSIONS };

interface UseTranscriptUploadOptions {
  pageName: string;
  entityId: string;
  activityId: string;
  activity: ActivityData;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseTranscriptUploadReturn {
  uploadTranscript: (file: File) => Promise<void>;
  isUploading: boolean;
  error: Error | null;
}

export const useTranscriptUpload = ({
  pageName,
  entityId,
  activityId,
  activity,
  onSuccess,
  onError
}: UseTranscriptUploadOptions): UseTranscriptUploadReturn => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadAttachmentMutation = useAPIMutation<
    { data: FormData },
    AttachmentUploadResponse
  >("attachments", { method: "POST" });

  const updateActivityMutation = useUpdateActivityMutation(pageName);

  const uploadAttachmentMutationRef = useRef(uploadAttachmentMutation);
  uploadAttachmentMutationRef.current = uploadAttachmentMutation;

  const updateActivityMutationRef = useRef(updateActivityMutation);
  updateActivityMutationRef.current = updateActivityMutation;

  const uploadTranscript = useCallback(
    async (file: File) => {
      if (!isValidTranscriptFile(file)) {
        const validationError = new Error(
          t("activities.transcript.invalidFileType")
        );
        setError(validationError);
        onError?.(validationError);
        return;
      }

      setIsUploading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const uploadResponse = await uploadAttachmentMutationRef.current.mutateAsync({
          data: formData
        });

        const updatedNotes = {
          ...activity.notes,
          hasTranscript: true,
          transcriptAttachmentId: uploadResponse.id
        };

        await updateActivityMutationRef.current.mutateAsync({
          pathParams: { entityId, activityId },
          data: {
            dealId: activity.dealId,
            leadId: activity.leadId,
            notes: updatedNotes,
            type: activity.type,
            tags: activity.tags,
            peopleIds: activity.peopleIds,
            companyId: activity.companyId
          } as ActivityData
        });

        toast.success(t("activities.transcript.uploadSuccess"));
        onSuccess?.();
      } catch (err) {
        const uploadError =
          err instanceof Error ? err : new Error("Upload failed");
        setError(uploadError);
        toast.error(t("activities.transcript.uploadFailed"));
        onError?.(uploadError);
      } finally {
        setIsUploading(false);
      }
    },
    [entityId, activityId, activity, onSuccess, onError, t]
  );

  return { uploadTranscript, isUploading, error };
};

interface UseTranscriptDownloadOptions {
  attachmentId: string | undefined;
  meetingTitle?: string;
  enabled?: boolean;
}

interface UseTranscriptDownloadReturn {
  downloadTranscript: () => void;
  isDownloading: boolean;
}

export const useTranscriptDownload = ({
  attachmentId,
  meetingTitle,
  enabled = true
}: UseTranscriptDownloadOptions): UseTranscriptDownloadReturn => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const { refetch } = useAPI(
    "attachments/id",
    {
      pathParams: { attachmentId: attachmentId || "" },
      responseType: "blob"
    },
    {
      enabled: false
    }
  );

  const downloadTranscript = useCallback(async () => {
    if (!attachmentId || !enabled) return;

    setIsDownloading(true);

    try {
      const result = await refetch();

      if (result.data) {
        const blob = result.data as Blob;
        const filename = generateTranscriptFilename(meetingTitle);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error(t("activities.transcript.downloadError"));
    } finally {
      setIsDownloading(false);
    }
  }, [attachmentId, meetingTitle, enabled, refetch, t]);

  return { downloadTranscript, isDownloading };
};
